import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { Note } from 'models/ComunicationHub';
import { supplierApi } from 'api/supplier';
import { dealsApi } from 'api/deals';
import { leadsApi } from 'api/leads';
import { brandApi } from 'api/brand';
import { clientApi } from 'api/client';
import { purchaseOrderApi } from 'api/purchaseOrder';

export interface PaginatedResponse<T> {
  items: T;
}

export interface INoteMapping {
  notes: Record<string, Note[]>;
  pinnedNotes: Record<string, Note[]>;
}

export interface INoteContent {
  content: INoteMapping[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export interface NoteQueryArgs {
  page?: number;
  size?: number;
  entityId: string;
  entityName: string;
  filterBy: string;
}

export const communicationHubApi = createApi({
  tagTypes: ['NOTE', 'NOTE_LIST', 'PINNED_NOTE_LIST'],
  reducerPath: 'communicationHubApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    noteCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/communication-hub/note',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER_LIST', 'SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
          dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
          dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
          dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: (result, error, arg) => {
        return arg.attachmentTypes?.length ? [] : ['NOTE_LIST', 'PINNED_NOTE_LIST'];
      },
    }),
    noteUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/communication-hub/note',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER_LIST', 'SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
          dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
          dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
          dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: (result, error, arg) => {
        return arg.attachmentTypes?.length ? [] : ['NOTE_LIST', 'PINNED_NOTE_LIST'];
      },
    }),
    pinnedNoteList: builder.query<
      any,
      {
        entityId: string;
        entityName: string;
        filterBy: string;
      }
    >({
      query: (args) => ({
        url: 'wrd/api/communication-hub/pinned',
        params: args,
      }),
      transformResponse: (response: any) => {
        const { pinnedNotes = {}, managers = {} } = response?.data || {};

        if (Object.keys(pinnedNotes || {}).length) {
          Object.values(pinnedNotes || {})
            .flat()
            .forEach((pinnedNote: any) => {
              pinnedNote.user = managers[pinnedNote.userId];
            });
        }
        return response.data?.pinnedNotes;
      },
      providesTags: () => ['PINNED_NOTE_LIST'],
    }),
    infinityNoteList: builder.infiniteQuery<INoteContent, NoteQueryArgs, number>({
      infiniteQueryOptions: {
        // Must provide a default initial page param value
        initialPageParam: 0,
        // Optionally limit the number of cached pages
        maxPages: 3,
        // Must provide a `getNextPageParam` function
        getNextPageParam: (lastPage, allPages, lastPageParam, _allPageParams) => {
          return lastPageParam < lastPage.totalPages - 1 ? lastPageParam + 1 : undefined;
        },
        // Optionally provide a `getPreviousPageParam` function
        getPreviousPageParam: (firstPage, allPages, firstPageParam, _allPageParams) => {
          return firstPageParam > 0 ? firstPageParam - 1 : undefined;
        },
      },
      // The `query` function receives `{queryArg, pageParam}` as its argument
      query: ({ queryArg, pageParam }) => ({
        url: 'wrd/api/communication-hub',
        params: {
          page: pageParam,
          size: queryArg.size,
          entityId: queryArg.entityId,
          entityName: queryArg.entityName,
          filterBy: queryArg.filterBy,
        },
      }),
      transformResponse: (response: any) => {
        const content = response?.data?.content?.[0];
        if (!content) {
          return response?.data;
        }
        const { notes = {}, managers = {} } = content;

        if (Object.keys(notes || {}).length) {
          Object.values(notes || {})
            .flat()
            .forEach((note: any) => {
              note.user = managers[note.userId];
            });
        }
        return response.data;
      },
      providesTags: () => ['NOTE_LIST'],
    }),
    noteDelete: builder.mutation<any, { noteId: string }>({
      query: ({ noteId }) => ({
        url: `/wrd/api/communication-hub/note?noteId=${noteId}`,
        method: 'delete',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER_LIST', 'SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
          dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
          dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
          dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['NOTE_LIST', 'PINNED_NOTE_LIST'],
    }),
    noteUpdatePin: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/communication-hub/note/pin',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(supplierApi.util.invalidateTags(['SUPPLIER_LIST', 'SUPPLIER']));
          dispatch(dealsApi.util.invalidateTags(['DEAL', 'DEAL_LIST']));
          dispatch(leadsApi.util.invalidateTags(['LEAD', 'LEAD_LIST']));
          dispatch(brandApi.util.invalidateTags(['BRAND', 'BRAND_LIST']));
          dispatch(clientApi.util.invalidateTags(['CLIENT', 'CLIENT_LIST']));
          dispatch(purchaseOrderApi.util.invalidateTags(['PURCHASE_ORDER', 'PURCHASE_ORDER_LIST']));
        } catch {
          // TODO handle catch
        }
      },
      invalidatesTags: () => ['NOTE_LIST', 'PINNED_NOTE_LIST'],
    }),
  }),
});

export const {
  useNoteCreateMutation,
  useNoteUpdateMutation,
  useNoteUpdatePinMutation,
  useNoteDeleteMutation,
  usePinnedNoteListQuery,
  useInfinityNoteListInfiniteQuery,
} = communicationHubApi;
